/**
 * Top bar: editable workflow name, "Load template" menu, validation
 * indicator, test-run (disabled in v1 with a tooltip), save draft, and
 * undo/redo.
 */

import React from 'react';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  BoltIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Link } from 'react-router-dom';

import { useBuilder } from './state/context';
import { TEMPLATES } from './fixtures/templates';
import { saveDraft } from './state/persistence';
import { createEmptyState } from './state/reducer';
import type { ValidationIssue } from './types';

export function TopBar(props: { issues: ValidationIssue[] }) {
  const { state, dispatch, undo, redo, canUndo, canRedo } = useBuilder();
  const [flash, setFlash] = React.useState<string | null>(null);

  const errors = props.issues.filter((i) => i.level === 'error');
  const warnings = props.issues.filter((i) => i.level === 'warning');

  const onSave = () => {
    saveDraft(state);
    setFlash('Saved');
    setTimeout(() => setFlash(null), 1500);
  };

  return (
    <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 py-2">
      <Link
        to="/app/workflows"
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white"
        title="Back to drafts"
      >
        <ArrowLeftIcon className="h-3.5 w-3.5" />
        Drafts
      </Link>

      <input
        type="text"
        value={state.label}
        onChange={(e) => dispatch({ type: 'rename', label: e.target.value })}
        className="flex-1 rounded-md border border-transparent px-2 py-1 text-base font-semibold text-neutral-900 dark:text-white hover:border-neutral-200 dark:hover:border-white/10 focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
      />

      <div className="flex items-center gap-1">
        <Menu as="div" className="relative">
          <MenuButton className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1 text-xs text-neutral-700 dark:text-neutral-300 hover:border-primary-300 dark:hover:border-primary-500/30">
            <RectangleStackIcon className="h-3.5 w-3.5" /> Templates
          </MenuButton>
          <MenuItems
            anchor="bottom start"
            className="z-50 mt-1 w-64 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1 shadow-lg focus:outline-none"
          >
            {TEMPLATES.map((tpl) => (
              <MenuItem key={tpl.id}>
                {() => (
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({
                        type: 'replace-state',
                        state: {
                          ...tpl,
                          id: state.id,
                          label: tpl.label,
                          createdAt: state.createdAt,
                          updatedAt: Date.now()
                        }
                      })
                    }
                    className="flex w-full flex-col items-start gap-0.5 rounded px-2 py-1.5 text-left text-xs hover:bg-primary-50 dark:hover:bg-primary-500/10"
                  >
                    <span className="font-semibold text-neutral-900 dark:text-white">
                      {tpl.label}
                    </span>
                    <span className="text-[11px] text-neutral-500 dark:text-neutral-400 line-clamp-2">
                      {tpl.description}
                    </span>
                  </button>
                )}
              </MenuItem>
            ))}
            <MenuItem>
              {() => (
                <button
                  type="button"
                  onClick={() =>
                    dispatch({
                      type: 'replace-state',
                      state: {
                        ...createEmptyState(),
                        id: state.id,
                        label: state.label || 'Untitled workflow',
                        createdAt: state.createdAt
                      }
                    })
                  }
                  className="mt-1 flex w-full items-center gap-1 rounded border-t border-neutral-100 dark:border-neutral-700 px-2 py-1.5 text-left text-xs text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5"
                >
                  Clear canvas
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>

        <button
          type="button"
          onClick={undo}
          disabled={!canUndo}
          className="rounded-md p-1.5 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 disabled:opacity-40"
          title="Undo"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={!canRedo}
          className="rounded-md p-1.5 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 disabled:opacity-40"
          title="Redo"
        >
          <ArrowUturnRightIcon className="h-4 w-4" />
        </button>

        <ValidationBadge errors={errors.length} warnings={warnings.length} />

        <button
          type="button"
          disabled
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1 text-xs text-neutral-400 dark:text-neutral-500"
          title="Available once save is wired to the backend"
        >
          <BoltIcon className="h-3.5 w-3.5" />
          Test run
        </button>

        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2 py-1 text-xs font-semibold text-white hover:bg-primary-700"
        >
          <DocumentCheckIcon className="h-3.5 w-3.5" />
          {flash ?? 'Save draft'}
        </button>
      </div>
    </div>
  );
}

function ValidationBadge(props: { errors: number; warnings: number }) {
  if (props.errors === 0 && props.warnings === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
        <CheckCircleIcon className="h-3 w-3" />
        No issues
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        props.errors > 0
          ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300'
          : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
      )}
    >
      <ExclamationTriangleIcon className="h-3 w-3" />
      {props.errors > 0 ? `${props.errors} error${props.errors === 1 ? '' : 's'}` : ''}
      {props.errors > 0 && props.warnings > 0 && ' · '}
      {props.warnings > 0 ? `${props.warnings} warning${props.warnings === 1 ? '' : 's'}` : ''}
    </span>
  );
}
